import { createReducer, on } from '@ngrx/store';
import { BranchesList, BranchForm, StoreLocatorFeatureGroup } from '../uig-branches/uig-branches.models';
import * as UigBranchesActions from './uig-branches.actions';

export const uigBranchesFeatureKey = 'uigBranches';

export const UIG_BRANCHES_OVERVIEW = 'uigBranchesOverview';
export const UIG_BRANCH_DETAILS = 'uigBranchDetails';
export const UIG_BRANCH_DELETE = 'uigBranchDelete';
export const UIG_BRANCH_CREATE_OR_UPDATE = 'uigBranchCreateOrUpdate';
export const UIG_BRANCH_STORE_FEATURE_GROUP = 'uigBranchStoreFeatureGroup';

export interface UigBranchesState {
  [UIG_BRANCHES_OVERVIEW]: {
    uigBranchesOverview: BranchesList;
    isLoading: boolean;
    error?: Error;
  };
  [UIG_BRANCH_DETAILS]: {
    branchForm: BranchForm;
    isLoading: boolean;
    error?: Error;
  };
  [UIG_BRANCH_DELETE]: {
    isRunning: boolean;
    success: boolean;
    error?: boolean;
  };
  [UIG_BRANCH_CREATE_OR_UPDATE]: {
    isRunning: boolean;
    success: boolean;
    error?: boolean;
  };
  [UIG_BRANCH_STORE_FEATURE_GROUP]: {
    featureGroups: StoreLocatorFeatureGroup[];
    isLoading: boolean;
    error?: Error;
  };
}

export const initialState: UigBranchesState = {
  [UIG_BRANCHES_OVERVIEW]: {
    uigBranchesOverview: null,
    isLoading: false,
    error: null,
  },
  [UIG_BRANCH_DETAILS]: {
    branchForm: null,
    isLoading: false,
    error: null,
  },
  [UIG_BRANCH_DELETE]: {
    isRunning: false,
    success: false,
    error: false,
  },
  [UIG_BRANCH_CREATE_OR_UPDATE]: {
    isRunning: false,
    success: false,
    error: false,
  },
  [UIG_BRANCH_STORE_FEATURE_GROUP]: {
    featureGroups: null,
    isLoading: false,
    error: null,
  }
};

export const uigBranchesReducer = createReducer(
  initialState,
  on(UigBranchesActions.getUigBranchesOverview,
    (state) => ({
      ...state,
      [UIG_BRANCHES_OVERVIEW]: {
        uigBranchesOverview: null,
        isLoading: true,
        error: null,
      },
    })),
  on(UigBranchesActions.getUigBranchesOverviewSuccess,
    (state, action) => ({
      ...state,
      [UIG_BRANCHES_OVERVIEW]: {
        uigBranchesOverview: action.uigBranchesOverview,
        isLoading: false,
        error: null,
      },
    })
  ),
  on(UigBranchesActions.getUigBranchesOverviewFailure,
    (state, action) => ({
      ...state,
      [UIG_BRANCHES_OVERVIEW]: {
        uigBranchesOverview: null,
        isLoading: false,
        error: action.error,
      },
    })
  ),
  on(UigBranchesActions.getUigBranchDetails,
    (state) => ({
      ...state,
      [UIG_BRANCH_DETAILS]: {
        branchForm: null,
        isLoading: true,
        error: null,
      },
    })),
  on(UigBranchesActions.getUigBranchDetailsSuccess,
    (state, action) => ({
      ...state,
      [UIG_BRANCH_DETAILS]: {
        branchForm: action.branch,
        isLoading: false,
        error: null,
      },
    })
  ),
  on(UigBranchesActions.getUigBranchDetailsFailure,
    (state, action) => ({
      ...state,
      [UIG_BRANCH_DETAILS]: {
        branchForm: null,
        isLoading: false,
        error: action.error,
      },
    })
  ),
  on(UigBranchesActions.deleteBranch,
    (state) => ({
      ...state,
      [UIG_BRANCH_DELETE]: {
        isRunning: true,
        success: false,
        error: false,
      },
    })
  ),
  on(UigBranchesActions.deleteBranchSuccess,
    (state) => ({
      ...state,
      [UIG_BRANCH_DELETE]: {
        isRunning: false,
        success: true,
        error: false,
      },
    })
  ),
  on(UigBranchesActions.deleteBranchFailure,
    (state) => ({
      ...state,
      [UIG_BRANCH_DELETE]: {
        isRunning: false,
        success: false,
        error: true,
      },
    })
  ),
  on(UigBranchesActions.deleteBranchReset,
    (state) => ({
      ...state,
      [UIG_BRANCH_DELETE]: {
        isRunning: false,
        success: false,
        error: false,
      },
    })
  ),
  on(UigBranchesActions.createOrUpdateBranch,
    (state) => ({
      ...state,
      [UIG_BRANCH_CREATE_OR_UPDATE]: {
        isRunning: true,
        success: false,
        error: false,
      },
    })
  ),
  on(UigBranchesActions.createOrUpdateBranchSuccess,
    (state) => ({
      ...state,
      [UIG_BRANCH_CREATE_OR_UPDATE]: {
        isRunning: false,
        success: true,
        error: false,
      },
    })
  ),
  on(UigBranchesActions.createOrUpdateBranchFailure,
    (state) => ({
      ...state,
      [UIG_BRANCH_CREATE_OR_UPDATE]: {
        isRunning: false,
        success: false,
        error: true,
      },
    })
  ),
  on(UigBranchesActions.createOrUpdateBranchReset,
    (state) => ({
      ...state,
      [UIG_BRANCH_CREATE_OR_UPDATE]: {
        isRunning: false,
        success: false,
        error: false,
      },
    })
  ),
  on(UigBranchesActions.getUigStoreFeatureGroups,
    (state) => ({
      ...state,
      [UIG_BRANCH_STORE_FEATURE_GROUP]: {
        featureGroups: null,
        isLoading: true,
        error: null,
      },
    })),
  on(UigBranchesActions.getUigStoreFeatureGroupsSuccess,
    (state, action) => ({
      ...state,
      [UIG_BRANCH_STORE_FEATURE_GROUP]: {
        featureGroups: action.featureGroups,
        isLoading: false,
        error: null,
      },
    })
  ),
  on(UigBranchesActions.getUigStoreFeatureGroupsFailure,
    (state, action) => ({
      ...state,
      [UIG_BRANCH_STORE_FEATURE_GROUP]: {
        featureGroups: null,
        isLoading: false,
        error: action.error,
      },
    })
  ),
);